import type { Primitive } from 'utility-types'

import { decodeFromBase64, encodeToBase64, type ReadonlyRecord } from '../../utils'

import { isUnknownWebStorageSerializedWrappedValue } from './Validators'

type RecursivePrimitive = Primitive | readonly RecursivePrimitive[] | ReadonlyRecord<string, Primitive | readonly RecursivePrimitive[]>
type WebStorageSerializedWrappedValue<ContainedValue> = readonly [version: string, containedValue: ContainedValue]

export type UnknownWebStorageSerializedWrappedValue = WebStorageSerializedWrappedValue<unknown>

/**
 * This webstorage manipulation class ensures all values are:
 *  - Centrally typed
 *  - Properly serialized and deserialized
 */
export abstract class BaseWebStorage<Value, Serializable extends RecursivePrimitive> {
    protected abstract readonly key: string

    protected abstract readonly fallbackValue: Value

    /**
     * Certain storage implementations may choose to ignore version mismatches,
     * as the serialized values from one version to another will not cause issues
     */
    protected ignoreVersionMismatch: boolean = false

    constructor (private readonly storage: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>, private readonly version: string) {}

    private wrapValue (value: Value): WebStorageSerializedWrappedValue<Serializable> {
        return [this.version, this.toSerializable(value)]
    }

    private doesVersionMatch ([version]: UnknownWebStorageSerializedWrappedValue): boolean {
        return this.ignoreVersionMismatch || version === this.version
    }

    private getValue (): string | null {
        return this.storage.getItem(this.key)
    }

    private setValue (value: string): void {
        this.storage.setItem(this.key, value)
    }

    protected remove (): void {
        this.storage.removeItem(this.key)
    }

    /**
     * Converts the output value into a serialized value (so it can be sent into the localStorage)
     */
    protected abstract toSerializable (value: Value): Serializable

    /**
     * Validate that an unknown value (that comes from localStorage) is a valid serialized value
     */
    protected abstract isValidSerializable (unknownValue: unknown): unknownValue is Serializable

    /**
     * Converts the already validated serialized value (that comes from localStorage) into the wanted output type
     */
    protected abstract fromSerializable (serialized: Serializable): Value

    protected persistSerialize (value: Value): void {
        const stringfied = JSON.stringify(this.wrapValue(value))
        const content = encodeToBase64(stringfied)
        this.setValue(content)
    }

    protected retrieveDeserialize (): Value {
        let value: Value = this.fallbackValue

        const raw = this.getValue()

        if (raw === null) {
            return value
        }

        try {
            const content = decodeFromBase64(raw)
            const wrappedJson = JSON.parse(content) as unknown

            if (isUnknownWebStorageSerializedWrappedValue(wrappedJson) && this.doesVersionMatch(wrappedJson) && this.isValidSerializable(wrappedJson[1])) {
                value = this.fromSerializable(wrappedJson[1])
            }
        } catch {}

        return value
    }
}
